import { format } from "date-fns";
import { useEffect, useState } from "react";
import { DataGrid, Tooltip } from "../../../components";
import { filtrosUrl, PermisoesHelper } from "../../../helpers";
import { ListLayout } from "../../../layouts";
import {
  history,
  notification,
  OperacaoFiscalService,
  PedidosService,
  PessoaService,
} from "../../../services";
import { formatDate, formatPrice, responseErros } from "../../../utils";
import { FormFiltroAvancado, ImportarOrcamentoDialog } from "./components";

const ListPedidosView = () => {
  const [loading, setLoading] = useState(false);
  const [pedidos, setPedidos] = useState([]);
  const [listaOperacoesFiscais, setListaOperacoesFiscais] = useState([]);
  const [orcamentoSelecionado, setOrcamentoSelecionado] = useState([]);
  const [openImportarOrcamentoDialog, setOpenImportarOrcamentoDialog] =
    useState(false);
  const [selecionados, setSelecionados] = useState([]);
  const [rowCount, setRowCount] = useState(null);
  const [listaPessoas, setListaPessoas] = useState([]);
  const pedidosService = new PedidosService();
  const pessoaService = new PessoaService();
  const operacaoFiscalService = new OperacaoFiscalService();
  const permissoesHelper = new PermisoesHelper();
  permissoesHelper.validarPermisao("pedidos-visualizar");
  const buttonSelectOpcoes = [
    { permission: true, descricao: "Importar Orçamento" },
  ];
  const status = [
    {
      label: "Aberto",
      nome: "ABERTO",
      cor: "#4FBB53",
    },
    {
      label: "Fechado",
      nome: "FECHADO",
      cor: "#004780",
    },
    {
      label: "Parcialmente Fechado",
      nome: "PARCIALMENTE_FECHADO",
      cor: "#EA9D2B",
    },
    {
      label: "Cancelado",
      nome: "CANCELADO",
      cor: "#DC3545",
    },
  ];
  const colunas = [
    {
      field: "status",
      headerName: "Status",
      width: 100,
      renderCell: (params) => {
        switch (params.value) {
          case "ABERTO":
            return (
              <Tooltip title="Aberto" arrow placement="right">
                <i
                  className="ph-fill ph-circle"
                  style={{ color: "#4FBB53", fontSize: 18 }}
                ></i>
              </Tooltip>
            );
          case "FECHADO":
            return (
              <Tooltip title="Fechado" arrow placement="right">
                <i
                  className="ph-fill ph-circle"
                  style={{ color: "#004780", fontSize: 18 }}
                ></i>
              </Tooltip>
            );
          case "PARCIALMENTE_FECHADO":
            return (
              <Tooltip title="Parcialmente Fechado" arrow placement="right">
                <i
                  className="ph-fill ph-circle"
                  style={{ color: "#EA9D2B", fontSize: 18 }}
                ></i>
              </Tooltip>
            );
          case "CANCELADO":
            return (
              <Tooltip title="Cancelado" arrow placement="right">
                <i
                  className="ph-fill ph-circle"
                  style={{ color: "#DC3545", fontSize: 18 }}
                ></i>
              </Tooltip>
            );
          default:
            return;
        }
      },
    },
    {
      field: "serieDocumento",
      headerName: "Série",
      width: 100,
    },
    {
      field: "numeroDocumento",
      headerName: "Nº Documento",
      flex: 100,
      filtrar: true,
    },
    {
      field: "operacaoFiscalId",
      headerName: "Operação Fiscal",
      flex: 110,
      valueGetter: (params) =>
        listaOperacoesFiscais.find((item) => item.id == params.value)
          ?.descricao,
    },
    {
      field: "dataEmissao",
      headerName: "Data de Emissão",
      width: 150,
      valueGetter: (params) =>
        format(formatDate.received(params.value), "dd/MM/yyyy"),
    },
    {
      field: "pessoaClienteId",
      headerName: "Cliente",
      width: 250,
      sortable: false,
      valueGetter: (params) =>
        listaPessoas.find((item) => item.id == params.value)?.nomeRazaoSocial,
    },
    {
      field: "itens",
      headerName: "Total do Pedido",
      flex: 200,
      sortable: false,
      valueGetter: (params) => {
        const totalPedido =
          params.value
            .map((item) => item.valorUnitario * item.quantidade)
            .reduce((acumulador, total) => acumulador + total, 0) +
          params.row.acrescimo -
          params.row.desconto +
          params.row.despesas;
        return formatPrice(totalPedido);
      },
    },
  ];

  useEffect(() => {
    const filtros = filtrosUrl.get();
    if (filtros.status) {
      const status = filtros.status.split(",");
      buscarPesquisaAvancada({ ...filtros, status });
    } else {
      buscarPedidos(filtros);
    }
    buscarOperacoesFiscais();
  }, []);

  const buscarPedidos = async function (filtro) {
    setLoading(true);
    const result = await pedidosService.getAll(filtro);
    if (!result.isAxiosError) {
      setPedidos(result.data.rows);
      setRowCount(result.data.count);
      buscarRelacionamentosPedido(result.data.rows);
      filtrosUrl.set(filtro);
    } else {
      responseErros(result);
    }
    setLoading(false);
  };

  const buscarRelacionamentosPedido = (listaDocumento) => {
    const idsPessoas = [
      ...new Set(
        listaDocumento
          .map(({ pessoaClienteId }) => (pessoaClienteId ? pessoaClienteId : 0))
          .filter(
            (pessoaClienteId, index, listaDocumento) =>
              listaDocumento.indexOf(pessoaClienteId) === index,
          ),
      ),
    ];
    buscarPessoas(idsPessoas);
  };

  const buscarPessoas = (listaIds) => {
    if (listaIds.length) {
      const filtro = {
        id: listaIds,
        nonPaginated: true,
        attributes: ["id", "nomeRazaoSocial"],
        disabledInclude: true,
      };
      pessoaService.getAll(filtro).then((result) => {
        if (!result.isAxiosError) {
          setListaPessoas(result.data);
        } else {
          responseErros(result);
        }
      });
    }
  };

  const buscarOperacoesFiscais = (filtros) => {
    const filtro = {
      ...filtros,
      nonPaginated: true,
    };
    operacaoFiscalService.getAll(filtro).then((result) => {
      if (!result.isAxiosError) {
        setListaOperacoesFiscais(result.data);
      } else {
        responseErros(result);
      }
    });
  };

  const onClickCadastrarPedidos = function (descricao) {
    switch (descricao) {
      case "Importar Orçamento":
        setOpenImportarOrcamentoDialog(true);
        break;
      default:
        history.push("/faturamento/pedidos/create");
        break;
    }
  };

  const onClickEditPedidos = function (id) {
    history.push(`/faturamento/pedidos/${id}`);
  };

  const buscarPesquisaAvancada = async (filter) => {
    const porIntervalo = [];
    const filtrosAvancadosTemp = {
      ...filter,
    };
    if (
      filtrosAvancadosTemp.dataEmissaoStart &&
      filtrosAvancadosTemp.dataEmissaoEnd
    ) {
      porIntervalo.push({
        coluna: "dataEmissao",
        de: filtrosAvancadosTemp.dataEmissaoStart,
        ate: filtrosAvancadosTemp.dataEmissaoEnd,
      });
    }
    if (filtrosAvancadosTemp.totalStart && filtrosAvancadosTemp.totalEnd) {
      porIntervalo.push({
        coluna: "totalVenda",
        de: parseFloat(filtrosAvancadosTemp.totalStart),
        ate: parseFloat(filtrosAvancadosTemp.totalEnd),
      });
    }
    delete filtrosAvancadosTemp.dataEmissaoStart;
    delete filtrosAvancadosTemp.dataEmissaoEnd;
    delete filtrosAvancadosTemp.totalStart;
    delete filtrosAvancadosTemp.totalEnd;

    setLoading(true);
    const result = await pedidosService.getAllFiltroAvancado({
      ...filtrosAvancadosTemp,
      restritiva: true,
      porIntervalo,
    });
    if (!result.isAxiosError) {
      setPedidos(result.data.rows);
      setRowCount(result.data.count);
      buscarRelacionamentosPedido(result.data.rows);
      filtrosUrl.set(filter);
    } else {
      responseErros(result);
    }
    setLoading(false);
  };

  const sendServerDatagrid = (props) => {
    if (props.tipoFiltro == "simples") {
      if (props.filtros?.status) {
        delete props.filtros.status;
      }
      buscarPedidos(props.filtros);
    } else {
      buscarPesquisaAvancada(props.filtros);
    }
  };

  const onClickCancelarPedido = async function (id) {
    const pedido = pedidos.find((item) => item.id === id);
    if (pedido) {
      if (pedido.status === "CANCELADO") {
        return notification.alertaGenericos("Esse pedido já está cancelado");
      }
      const retornoAlerta = await notification.confirmacao(
        "Cancelar!",
        "Tem certeza que deseja cancelar esse pedido?",
      );
      if (retornoAlerta.isConfirmed) {
        setLoading(true);
        const response = await pedidosService.cancelar(pedido.id);
        setLoading(false);
        if (!response.isAxiosError) {
          notification.alteracaoSucesso();
          buscarPedidos();
        } else {
          responseErros(response);
        }
      }
    }
  };

  return (
    <ListLayout
      title="Pedidos"
      loading={loading}
      onClickCadastrar={onClickCadastrarPedidos}
      buttonSelectOpcoes={buttonSelectOpcoes}
      permissaoButton={permissoesHelper.temPermisao("pedidos-cadastrar")}
    >
      <DataGrid
        rows={pedidos}
        columns={colunas}
        onClickEdit={onClickEditPedidos}
        onClickDelete={onClickCancelarPedido}
        visualizarShow={permissoesHelper.temPermisao("pedidos-visualizar")}
        editShow={permissoesHelper.temPermisao("pedidos-editar")}
        deleteShow={permissoesHelper.temPermisao("pedidos-excluir")}
        rowCount={rowCount}
        paginationMode={"server"}
        sendServer={sendServerDatagrid}
        FormFiltroAvancado={FormFiltroAvancado}
        listaStatus={status}
      />
      <ImportarOrcamentoDialog
        openImportarOrcamentoDialog={openImportarOrcamentoDialog}
        setOpenImportarOrcamentoDialog={setOpenImportarOrcamentoDialog}
        setSelecionados={setSelecionados}
        selecionados={selecionados}
        setOrcamentoSelecionado={setOrcamentoSelecionado}
        orcamentoSelecionado={orcamentoSelecionado}
        setLoading={setLoading}
        loading={loading}
      />
    </ListLayout>
  );
};

export default ListPedidosView;
